import { AuthMachineStatus, HeaderConstants } from "@/constants";
import { useAuthMachineStore } from "@/store/authMachine/store";
import { AuthEvent, authEventBus } from "@/utils/authEventBus";
import { sessionManager } from "@/utils/sessionManager";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import axios, { AxiosError } from "axios";
import posthog from "posthog-js";

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
}

export type GetAccessTokenFn = () => Promise<AuthTokens | null>;

export interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export type APIInstance = AxiosInstance;

// Purely for backend observability logging (pre-auth ExecutionContext.user_id), not auth.
const getCurrentUserId = (): string | undefined => useAuthMachineStore.getState().state.user?.id;

// Shared by the request interceptor and the 401-retry path, since the retry path
// calls `api(originalRequest)` directly and bypasses the request interceptor.
const applyRequestHeaders = (
  headers: InternalAxiosRequestConfig["headers"],
  accessToken: string
): void => {
  headers.Authorization = `Bearer ${accessToken}`;

  const sessionHeaders = sessionManager.getSessionHeaders();
  Object.assign(headers, sessionHeaders);

  const userId = getCurrentUserId();
  if (userId) {
    headers[HeaderConstants.USER_ID_HEADER] = userId;
  }

  // Lets backend logs link to the PostHog session replay; omitted when PostHog has no
  // active session. Guarded so a future SDK throw can't fail the request itself.
  try {
    const posthogSessionId = posthog.get_session_id();
    if (posthogSessionId) {
      headers[HeaderConstants.POSTHOG_SESSION_ID_HEADER] = posthogSessionId;
    }
  } catch {
    // Optional header — nothing to do.
  }
};

export const getBaseURL = (version: "v1" | "v2" = "v1"): string => {
  if (import.meta.env.VITE_API_BASE_URL) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    // Replace version in base URL if it exists, otherwise append version
    if (baseUrl.includes("/api/v")) {
      return baseUrl.replace(/\/api\/v\d+/, `/api/${version}`);
    }
    return `${baseUrl}/api/${version}`;
  }
  const basePath =
    process.env.NODE_ENV === "production" || import.meta.env.VITE_USE_PROXY === "true"
      ? "/api"
      : "http://localhost:8000/api";
  return `${basePath}/${version}`;
};

const SESSION_EXPIRED_ERROR_MESSAGE = "Session expired";

/**
 * True for the error thrown when a request is attempted on an expired session. The session
 * flow handles re-authentication on its own, so callers use this to stay silent rather than
 * surfacing it as a request failure.
 */
export const isSessionExpiredError = (error: Error | undefined): boolean =>
  error instanceof Error && error.message === SESSION_EXPIRED_ERROR_MESSAGE;

export const createAPI = (
  getAccessToken: GetAccessTokenFn,
  version: "v1" | "v2" = "v1"
): APIInstance => {
  const api = axios.create({
    baseURL: getBaseURL(version),
  });

  api.interceptors.request.use(async (config: ExtendedAxiosRequestConfig) => {
    // Read live rather than closing over a value — this instance is created once
    // and reused for the component's lifetime, so a captured auth status would go stale.
    const authStatus = useAuthMachineStore.getState().state.status;
    // Don't intercept if this is a retry and session is expired
    if (config._retry && authStatus === AuthMachineStatus.EXPIRED) {
      throw new Error(SESSION_EXPIRED_ERROR_MESSAGE);
    }

    try {
      const tokens: AuthTokens | null = await getAccessToken();
      if (!tokens?.access_token) {
        const unauthorizedError = new Error("No valid session") as AxiosError;
        unauthorizedError.response = {
          status: 401,
          data: { detail: "No valid session" },
          statusText: "Unauthorized",
          headers: {},
          config: config,
        };
        unauthorizedError.config = config;
        throw unauthorizedError;
      }

      applyRequestHeaders(config.headers, tokens.access_token);
      return config;
    } catch (error) {
      console.error("[API] Request interceptor error:", error);
      throw error;
    }
  });

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

      console.error("[API] Error:", {
        message: error.message,
        status: error.response?.status,
        url: originalRequest?.url,
      });

      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const tokens: AuthTokens | null = await getAccessToken();
          if (tokens?.access_token) {
            applyRequestHeaders(originalRequest.headers, tokens.access_token);
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error("[API] Token refresh failed:", refreshError);
        }
      }

      if (error.response?.status === 401 && originalRequest?._retry) {
        authEventBus.emit(AuthEvent.LOGOUT);
      }
      return Promise.reject(error);
    }
  );

  return api;
};

// Create a public API instance without authentication
export const createPublicAPI = (version: "v1" | "v2" = "v1"): APIInstance => {
  const publicApi = axios.create({
    baseURL: getBaseURL(version),
  });

  return publicApi;
};

// Hook to use the public API instance
export const usePublicAPI = (version: "v1" | "v2" = "v1"): APIInstance => {
  return createPublicAPI(version);
};
